import React, { useEffect } from "react";
import { currentUrl } from "@/lib/ssrData";

/**
 * SeoHead — per-page <title>/<meta>/<link> tags plus optional JSON-LD.
 *
 * React 19 hoists any <title>, <meta> and <link> rendered anywhere in the
 * tree into the real document <head> automatically (no react-helmet
 * needed). The last one rendered on a given navigation wins, which is
 * exactly what we want — each page just renders its own SeoHead and the
 * previous page's tags are replaced.
 *
 * One thing React's hoisting does NOT do: dedupe against a tag that was
 * already in the static index.html rather than rendered by React (it has
 * no way to know they're "the same" tag). index.html ships a fallback
 * <meta name="description" data-seo="default"> for pages that never
 * mount a SeoHead — any page that DOES mount one removes that fallback on
 * mount, so a crawler never sees two conflicting descriptions.
 *
 * `jsonLd` (a plain object or array of objects) is rendered as one or more
 * <script type="application/ld+json"> tags — these are NOT auto-hoisted,
 * but structured data doesn't need to be in <head> to be read.
 */
export default function SeoHead({ title, description, canonical, image, jsonLd, noindex }) {
  // currentUrl() is window.location in a browser and the URL the server was
  // asked for otherwise, so a server-rendered page carries the canonical the
  // browser would have built rather than none. See lib/ssrData.js.
  const url = canonical || currentUrl();
  const ldBlocks = !jsonLd ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  useEffect(() => {
    if (!description) return;
    // React 19 adopts/rewrites the static index.html fallback tag as part
    // of its own <meta> bookkeeping, stripping the data-seo marker we'd
    // otherwise match on — so instead just remove any description tag
    // that isn't the content we actually want, regardless of attributes.
    document.querySelectorAll('meta[name="description"]').forEach((el) => {
      if (el.getAttribute("content") !== description) el.remove();
    });
  }, [description]);

  return (
    <>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {url && <link rel="canonical" href={url} />}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content="website" />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {ldBlocks.map((block, i) => (
        // Escaping "<" is belt-and-braces here — React renders this as a
        // text node via the DOM API, not by parsing an HTML string, so
        // "</script>" inside tenant-editable content (a testimonial, an
        // FAQ answer) can't actually break out of the tag today. But the
        // Python bot-prerender path in prerender.py builds this same JSON-LD
        // as a raw HTML string and genuinely needs the escape, so it's kept
        // here too rather than leaving the two paths inconsistent.
        <script key={i} type="application/ld+json">{JSON.stringify(block).replace(/</g, "\\u003c")}</script>
      ))}
    </>
  );
}
